import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';
import { KeyMetricMetricType } from '@urbanx/agx.rex.client';
import dayjs from 'dayjs';
import { useCallback, useContext } from 'react';
import RexService from 'services/rexService';

export interface ExecutiveRunRateMetricsResponse {
  period: string;
  previousYearPeriod: string;
  metrics: RunRateMetric[];
}

export interface RunRateMetric {
  title: string;
  monthToDate: number;
  runRate: number;
  previousYearMonthToDate: number;
  percentageChange: number;
  metricType: KeyMetricMetricType;
}

type GetExecutiveRunRateMetricsKey = [
  'GetExecutiveRunRateMetrics',
  string, // month
];

const useExecutiveRunRateMetrics = (month: string, enabled: boolean) => {
  const { getAuthToken } = useContext(AuthContext);

  const getExecutiveRunRateMetrics = useCallback(
    async ([
      url,
      month,
    ]: GetExecutiveRunRateMetricsKey): Promise<ExecutiveRunRateMetricsResponse> => {
      try {
        const token = await getAuthToken();

        const service = new RexService(token);

        const result = await service.rexService.get(url, {
          month: dayjs(month).format('YYYY-MM'),
        });

        return result.data;
      } catch (error) {
        console.error('Error fetching executive run rate metrics:', error);
        throw new Error(`Failed to fetch executive run rate metrics for month: ${month}`);
      }
    },
    [getAuthToken]
  );

  const { data, isPending, isError } = useQuery<
    ExecutiveRunRateMetricsResponse,
    Error,
    ExecutiveRunRateMetricsResponse,
    GetExecutiveRunRateMetricsKey
  >({
    queryKey: ['GetExecutiveRunRateMetrics', month],
    queryFn: (queryInfo) => getExecutiveRunRateMetrics(queryInfo.queryKey),
    enabled: enabled,
  });

  return { data, isPending, isError };
};

export default useExecutiveRunRateMetrics;
